export const PROMOTION_WEEKDAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' },
];

const normalizeText = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const safePositiveInteger = (value, fallback = 1) => {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) && number > 0 ? number : fallback;
};

const normalizeUnit = (value = '') => {
  const unit = normalizeText(value);
  if (unit === 'gramo' || unit === 'gramos' || unit === 'gr') return 'g';
  if (unit === 'mililitro' || unit === 'mililitros') return 'ml';
  return unit || 'unidad';
};

export const isPromotionCategory = (category) => {
  const normalized = normalizeText(category);
  return normalized === 'promo' || normalized === 'promos' || normalized === 'promocion' || normalized === 'promociones';
};

export const getPromotionConfig = (product) => product?.config?.promotion || null;

export const isPromotionProduct = (product) => getPromotionConfig(product)?.enabled === true;

// El formato antiguo guardaba un solo producto en sourceProductId/quantity.
// El nuevo formato usa components[] para admitir tanto 2x1 como combos mixtos.
export const getPromotionComponents = (promotionOrProduct) => {
  const promotion = promotionOrProduct?.config?.promotion || promotionOrProduct || {};
  const rawComponents = Array.isArray(promotion.components)
    ? promotion.components
    : (promotion.sourceProductId ? [{
        sourceProductId: promotion.sourceProductId,
        sourceProductName: promotion.sourceProductName || '',
        quantity: promotion.quantity,
      }] : []);

  return rawComponents
    .map((component) => ({
      sourceProductId: String(component?.sourceProductId || '').trim(),
      sourceProductName: String(component?.sourceProductName || '').trim(),
      quantity: safePositiveInteger(component?.quantity),
    }))
    .filter((component) => component.sourceProductId);
};

export const isProductAvailableToday = (product, date = new Date()) => {
  if (!isPromotionProduct(product)) return true;
  const weekdays = Array.isArray(getPromotionConfig(product)?.weekdays)
    ? getPromotionConfig(product).weekdays.map(Number)
    : [];
  return weekdays.includes(date.getDay());
};

export const calculatePromotionCost = (sourceProduct, quantity) => (
  Number(sourceProduct?.costPrice || 0) * safePositiveInteger(quantity)
);

export const calculatePromotionComponentsCost = (components = [], products = []) => {
  const productById = new Map((products || []).map((product) => [product?.id, product]));
  return (components || []).reduce((total, component) => (
    total + calculatePromotionCost(
      productById.get(component?.sourceProductId),
      component?.quantity
    )
  ), 0);
};

export const buildPromotionRecipe = (resolvedComponents = []) => {
  const baseItems = new Map();
  const choiceGroups = new Map();
  const isMixedCombo = resolvedComponents.length > 1;

  resolvedComponents.forEach(({ sourceProduct, quantity }) => {
    const recipe = sourceProduct?.config?.recipe || {};
    const multiplier = safePositiveInteger(quantity);

    (Array.isArray(recipe.baseItems) ? recipe.baseItems : []).forEach((item) => {
      const productId = String(item.productId || item.id || '').trim();
      if (!productId) return;
      const unit = normalizeUnit(item.unit);
      const key = `${productId}::${unit}`;
      const qty = Number(item.qty ?? item.quantity ?? item.qtyPerUnit ?? 1) * multiplier;
      const existing = baseItems.get(key);
      if (existing) existing.qty += qty;
      else baseItems.set(key, { ...item, productId, unit, qty });
    });

    (Array.isArray(recipe.choiceGroups) ? recipe.choiceGroups : []).forEach((group) => {
      const unit = normalizeUnit(group.unit || 'unidad');
      const fromCategory = String(group.fromCategory || '').trim();
      const perPickQty = Number(group.perPickQty || 0);
      const key = `${normalizeText(fromCategory)}::${unit}::${perPickQty}`;
      const label = isMixedCombo
        ? `${sourceProduct.name || 'Producto'}: ${group.label || 'Opción'}`
        : (group.label || 'Opción');
      const min = Number(group.min || 0) * multiplier;
      const max = Number(group.max ?? group.min ?? 0) * multiplier;
      const existing = choiceGroups.get(key);
      if (existing) {
        existing.min += min;
        existing.max += max;
        if (!existing.labels.includes(label)) existing.labels.push(label);
      } else {
        choiceGroups.set(key, {
          labels: [label],
          min,
          max,
          perPickQty,
          unit,
          fromCategory,
          defaultProductId: group.defaultProductId || '',
        });
      }
    });
  });

  return {
    baseItems: Array.from(baseItems.values()),
    choiceGroups: Array.from(choiceGroups.values()).map((group, index) => ({
      id: `promo-choice-${index + 1}`,
      label: group.labels.join(' + '),
      min: group.min,
      max: Math.max(group.max, group.min),
      perPickQty: group.perPickQty,
      unit: group.unit,
      fromCategory: group.fromCategory,
      defaultProductId: group.defaultProductId,
    })),
  };
};

export const resolvePromotionProduct = (promotionProduct, products = []) => {
  if (!isPromotionProduct(promotionProduct)) return promotionProduct;

  const promotion = getPromotionConfig(promotionProduct);
  const components = getPromotionComponents(promotion);
  if (components.length === 0) return null;

  const productById = new Map((products || []).map((item) => [item?.id, item]));
  const resolvedComponents = components.map((component) => ({
    ...component,
    sourceProduct: productById.get(component.sourceProductId),
  }));
  if (resolvedComponents.some(({ sourceProduct }) => (
    !sourceProduct?.config?.recipe || sourceProduct.deleted === true || isPromotionProduct(sourceProduct)
  ))) return null;

  const recipe = buildPromotionRecipe(resolvedComponents);
  const canonicalComponents = resolvedComponents.map(({ sourceProduct, quantity }) => ({
    sourceProductId: sourceProduct.id,
    sourceProductName: sourceProduct.name || '',
    quantity,
  }));

  return {
    ...promotionProduct,
    costPrice: calculatePromotionComponentsCost(canonicalComponents, products),
    excludeFromEntries: true,
    config: {
      ...(promotionProduct.config || {}),
      recipe,
      promotion: {
        ...promotion,
        components: canonicalComponents,
      },
    },
    _promotionResolved: true,
  };
};
